/**
 * BMRCL dynamic-QR assembly.
 *
 * The gate QR is the static authorization token joined (with "#") to a dynamic
 * block carrying the current epoch as hex seconds. The gate compares that
 * timestamp to its OWN system clock and NACKs the QR when the difference exceeds
 * 30s, so the client must regenerate the dynamic block every few seconds against
 * a server-synced clock. Both functions are pure (no Date/DOM) so they stay
 * trivially testable; the component owns the timer + clock offset.
 */

/**
 * hexEpochSeconds renders an epoch-ms instant as lowercase 32-bit hex SECONDS
 * (e.g. 1587475074000 -> "5e9ef282"), zero-padded to 8 digits to match BMRCL's
 * fixed-width 32-bit parsing. `>>> 0` forces unsigned 32-bit (matches the gate).
 */
export function hexEpochSeconds(nowMs: number): string {
  return (Math.floor(nowMs / 1000) >>> 0).toString(16).padStart(8, '0');
}

/**
 * buildDynamicQrValue assembles the full gate-QR payload:
 *   `<staticBlock>#{<hex>||<lat>|<lng>|}`
 * QR_Status and Operator_Specific_Data are left empty per BMRCL's spec.
 */
export function buildDynamicQrValue(
  staticBlock: string,
  hex: string,
  lat: string,
  lng: string
): string {
  return `${staticBlock}#{${hex}||${lat}|${lng}|}`;
}

/**
 * isRenderableQr reports whether a pass carries enough to build a QR the gate could
 * actually accept.
 *
 * This guard exists because there was none: `buildDynamicQrValue('', ...)` happily
 * returns a well-formed `#{5e9ef282||lat|lng|}`, and qrcode.react encodes it without
 * complaint — so a pass whose authorization token had not been issued yet rendered as a
 * normal-looking, pulsing, counting-down "Live & valid" code that the turnstile would
 * reject. Showing a rider nothing is recoverable; showing them a confident fake is not.
 */
export function isRenderableQr(pass: {
  qrStaticBlock: string;
  qrDynamic: boolean;
  qrLat: string;
  qrLng: string;
}): boolean {
  if (!pass.qrStaticBlock) return false;
  // The dynamic block interpolates the operator's coordinates; without them the payload
  // is malformed in a way only the gate would discover.
  if (pass.qrDynamic && (!pass.qrLat || !pass.qrLng)) return false;
  return true;
}
